import type { ChildId } from './valueObjects/childId';
import type { MealContent } from './valueObjects/mealContent';
import type { MeasureDateTime } from './valueObjects/measureDateTime';
import type { MeasurerId } from './valueObjects/measurerId';
import type { MeasurerType } from './valueObjects/measurerType';
import type { ParentId } from './valueObjects/parentId';
import type { MealJsonPvt, MealJsonPub } from '@/types/domain/meal/entities/mealEntity';

import { EntityPub } from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 食事非公開プロパティ
 */
export type MealPropsPvt = {
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 測定ID */
  measurerId: MeasurerId,
  /** 測定タイプ */
  measurerType: MeasurerType,
  /** 測定日時 */
  measureDateTime: MeasureDateTime,
  /** 食事内容 */
  mealContent: MealContent,
}

/**
 * 食事公開プロパティ
 */
export type MealPropsPub = Record<string, never>;

/**
 * 食事エンティティ
 */
export class MealEntity extends EntityPub<
  MealPropsPvt,
  MealJsonPvt,
  MealPropsPub,
  MealJsonPub
> {
  /**
   * 削除タイプ
   */
  get deleteType(): DeleteType {
    return DeleteType.ParentChildId;
  }

  /**
   * 削除キー
   */
  get deleteKey(): string {
    return this.parentChildId;
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoAppMealMealEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.measureDateTime.toStringDateTime()}#${this.parentId.value}_${this.childId.value}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * サブキー
   */
  get subKey(): string {
    return `${this.parentId.value}_${this.childId.value}#${this.measureDateTime.toStringDateTime()}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * 親子ID
   */
  get parentChildId(): string {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 測定ID
   */
  get measurerId(): MeasurerId {
    return this._value.measurerId;
  }

  /**
   * 測定タイプ
   */
  get measurerType(): MeasurerType {
    return this._value.measurerType;
  }

  /**
   * 測定日時
   */
  get measureDateTime(): MeasureDateTime {
    return this._value.measureDateTime;
  }

  /**
   * 食事内容
   */
  get mealContent(): MealContent {
    return this._value.mealContent;
  }

  /**
   * Jsonデータにする
   * @returns データ
   */
  toJsonData(): MealJsonPvt {
    return {
      parentId: this.parentId.value,
      childId: this.childId.value,
      measurerId: this.measurerId.value,
      measurerType: this.measurerType.value,
      measureDateTime: this.measureDateTime.value.toJSON(),
      mealContent: this.mealContent.value,
    };
  }

  toJsonPubData(): MealJsonPub {
    return {};
  }
}
